import settings from '@solinkcorp/settings';
import Cookie from 'js-cookie';
import { array, object, string } from 'yup';
import { cloudFrontBase64ToBase64 } from '../../../utils/Base64Util';
import { type ShareCookie } from '../types/event.types';

export const SOLINK_CLOUD_COOKIE_DOMAIN = settings.domain_solink;
export const CLOUD_FRONT_COOKIE_NAME_POLICY = 'CloudFront-Policy';
export const CLOUD_FRONT_COOKIE_PREFIX = 'CloudFront-';

const AWS_STATEMENT_SCHEMA = object({
  Statement: array()
    .required()
    .min(1)
    .of(
      object({
        Resource: string().required(),
      }),
    ),
});

function getResource(policy: string | undefined): string | undefined {
  if (policy === undefined) return undefined;

  let marshalledPolicy: string;
  try {
    marshalledPolicy = atob(cloudFrontBase64ToBase64(policy));
  } catch (err) {
    console.warn(`could not decode AWS policy: ${err}`);
    return undefined;
  }

  let j: unknown;
  try {
    j = JSON.parse(marshalledPolicy);
  } catch (err) {
    console.warn(`could not unmarshal AWS resource: ${err}`);
    return undefined;
  }

  try {
    const validatedPolicy = AWS_STATEMENT_SCHEMA.validateSync(j);
    return validatedPolicy?.Statement[0].Resource;
  } catch (err) {
    console.warn(`could not validate AWS policy: ${err}`);
    return undefined;
  }
}

/**
 * mightCleanCloudFrontCookie might add, skip, and/or delete "CloudFront-" prefixed cookies.
 * Given a user and share page this functions will:
 *                   no user                   = pass the cookies through; set share specific cookies
 * user from customer A, share from customer A = ignore the share specific cookies
 * user from customer A, share from customer B = delete the root cookies from customer A
 *
 * @see SL-1558
 */
export function* mightCleanCloudFrontCookie(
  cookies: ShareCookie[],
  shareCustomerId: string,
  shareEventId: string,
) {
  const existingPolicy = Cookie.get(CLOUD_FRONT_COOKIE_NAME_POLICY);
  const newPolicy = cookies.find(
    (shareCookie) => shareCookie.name === CLOUD_FRONT_COOKIE_NAME_POLICY,
  );

  const r1 = getResource(existingPolicy);
  const r2 = getResource(newPolicy?.value);
  if (r1 === undefined || r2 === undefined || r1 === r2) {
    yield* cookies;
    return;
  }

  let useUserCookies = false;
  try {
    const u = new URL(r1);
    // Check if the existing user's CloudFront policy already covers this share's customer.
    // If so, the user's root cookies are sufficient and the share cookies can be skipped.
    useUserCookies = u.pathname.startsWith(`/${shareCustomerId}`);
  } catch (_err) {
    yield* cookies;
    return;
  }

  for (const shareCookie of cookies) {
    if (shareCookie.name.startsWith(CLOUD_FRONT_COOKIE_PREFIX)) {
      if (useUserCookies) {
        // Remove any stale share-specific cookies — duplicate CloudFront cookies with
        // the same name but different paths cause 403s from CloudFront.
        // The user's root cookies already cover this share path so removal is safe.
        Cookie.remove(shareCookie.name, {
          path: `/${shareCustomerId}/shares/${shareEventId}/`,
          domain: SOLINK_CLOUD_COOKIE_DOMAIN,
        });
        continue;
      } else {
        // Root path cookies will conflict with share path cookies so they must be removed.
        // This will break cloud-only playback for an authenticated route.
        // Refreshing an authenticated route will set the correct cookies.
        // See SL-1558
        Cookie.remove(shareCookie.name, { path: '/', domain: SOLINK_CLOUD_COOKIE_DOMAIN });
        yield shareCookie;
      }
    } else {
      yield shareCookie;
    }
  }
}
